import { inject, Injectable } from '@angular/core';
import { ActiveCartService, MultiCartService } from '@cart/data-access';
import { DeliveryTimeSlot, TimeSlotAdditionalParams, TimeSlotOption } from '@checkout-v2/const';
import {
  CheckoutActions,
  CheckoutApiService,
  DeliveryAddressFacade,
  ReceiptTypeFacade,
  ServicesFacade,
  TimeSlotsFacade,
} from '@checkout-v2/data-access';
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { UserIdService } from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, debounceTime, exhaustMap, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ApplyingBonusService, PickupService } from '../services';

@Injectable()
export class CustomerConsignmentEffects implements OnRunEffects {
  private readonly actions$ = inject(Actions);
  private readonly userIdService = inject(UserIdService);
  private readonly multiCartService = inject(MultiCartService);
  private readonly activeCartService = inject(ActiveCartService);
  private readonly checkoutApiService = inject(CheckoutApiService);
  private readonly deliveryAddressFacade = inject(DeliveryAddressFacade);
  private readonly receiptTypeFacade = inject(ReceiptTypeFacade);
  private readonly servicesFacade = inject(ServicesFacade);
  private readonly timeSlotsFacade = inject(TimeSlotsFacade);
  private readonly pickupService = inject(PickupService);
  private readonly applyingBonusService = inject(ApplyingBonusService);

  // Изменился контекст
  // Обновить кастомерконсаймент
  context$: Observable<CheckoutActions.UpdateCustomerConsignment> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_RECEIPT_TYPE_SUCCESS),
      switchMap(() =>
        combineLatest({
          // обязательное поле, поэтому фильтруем пустые значения
          deliveryModeId: this.receiptTypeFacade.getActiveReceiptTypeId().pipe(filter(Boolean)),
          activeAddress: this.deliveryAddressFacade.getActiveAddress(),
          activeService: this.servicesFacade.getServicesActiveOption(),
          selectedTimeSlot: this.timeSlotsFacade.getSelectedTimeSlot(),
          selectedTimeSlotAdditionalParams: this.timeSlotsFacade.getSelectedTimeSlotAdditionalParams(),
          activeShop: this.pickupService.activeShop$,
          applyingBonus: this.applyingBonusService.getApplyingBonus(),
        }).pipe(
          map(
            ({
              deliveryModeId,
              activeAddress,
              selectedTimeSlot,
              selectedTimeSlotAdditionalParams,
              activeService,
              activeShop,
              applyingBonus,
            }) => {
              return {
                deliveryModeId,
                deliveryAddressId: activeAddress?.id,
                deliveryTimeSlot:
                  selectedTimeSlot !== undefined
                    ? this.formatTimeSlotOption(selectedTimeSlot, selectedTimeSlotAdditionalParams)
                    : undefined,
                posName: activeShop?.name,
                pickupDate: activeShop?.pickupDate,
                services:
                  activeService !== undefined
                    ? [{ code: activeService.code, cost: activeService.minPrice }]
                    : undefined,
                serviceReason: activeService?.reason,
                bonusApplyingAmount: applyingBonus,
              };
            },
          ),
          map((data) => {
            return new CheckoutActions.UpdateCustomerConsignment(data);
          }),
        ),
      ),
    ),
  );

  // Обновить кастомерконсаймент
  // Отправить запрос на бекенд
  updateCustomerConsignment$: Observable<
    CheckoutActions.UpdateCustomerConsignmentSuccess | CheckoutActions.UpdateCustomerConsignmentFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT),
      debounceTime(600),
      map((action: CheckoutActions.UpdateCustomerConsignment) => action.payload),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => Boolean(cartId)),
      switchMap(([payload, cartId]) =>
        this.checkoutApiService.setCheckoutData(cartId, payload).pipe(
          map(() => new CheckoutActions.UpdateCustomerConsignmentSuccess(payload)),
          catchError((error) => of(new CheckoutActions.UpdateCustomerConsignmentFail(error))),
        ),
      ),
    ),
  );

  // Обновлен кастомерконсаймент
  // Пересчитать корзинку
  reloadCartOnUpdateCustomerConsignment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT_SUCCESS),
        switchMap(() =>
          combineLatest({
            userId: this.userIdService.getUserId(),
            cartId: this.activeCartService.getActiveCartId(),
          }).pipe(
            take(1),
            filter(({ cartId, userId }) => !!cartId && !!userId),
          ),
        ),
        map(({ cartId, userId }) =>
          this.multiCartService.loadCart({
            cartId: cartId,
            userId: userId,
            extraData: { active: true, refresh: true },
          }),
        ),
      ),
    { dispatch: false },
  );

  private formatTimeSlotOption(
    timeSlotOption: TimeSlotOption,
    additionalParams?: TimeSlotAdditionalParams,
  ): DeliveryTimeSlot {
    return {
      date: timeSlotOption.interval.clientInterval.date,
      interval: additionalParams?.customTimeFromTo ?? timeSlotOption.interval.clientInterval.timeFromTo,
      price: timeSlotOption.price.total,
      intervalType: timeSlotOption.interval.type,
    };
  }

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
